import React, { useState } from 'react';
import Breadcrumbs from '../common/Breadcrumbs';
import Permalink from '../common/Permalink';
import SubmissionsIndex from '../submissions/SubmissionsIndex';
import Row from '../common/Row';
import TimeStamp from '../common/TimeStamp';
import Icon from '../common/Icon';
import {
  UserContext,
  FilterFieldsContext,
  FormContext,
} from '../../hooks/Contexts';
import { userCan } from '../../helpers/rolePermissions';
import {
  determineFilterFields,
  determineInformer,
} from './constants/handleForm';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import FormMetaDataEntries from './form-meta-entries/FormMetaEntries';

const FormShowSubmissions = () => {
  const { data } = useLoaderData();
  const { current_user, permission, form, custom_filters } = data;
  const customFiltersAvailable = custom_filters.length > 0;
  const revalidator = useRevalidator();
  const [showMeta, setShowMeta] = useState(false);
  const [filterByCustom, setFilterByCustom] = useState(() => {
    // This boolean is used to set up the expected filter_fields (custom vs default)
    if (customFiltersAvailable) {
      // Must check filter_type from history state when navigating back/forward
      if (history.state?.name === 'submissions' && history.state?.query) {
        let encoded = Object.fromEntries(
          new URLSearchParams(history.state.query)
        );
        return encoded.filter_type === 'custom';
      }
      return true;
    }
    return false;
  });
  const informerField = determineInformer(form.fieldsets);
  const filter_fields = filterByCustom ? custom_filters : form.filter_fields;
  const filtFields = determineFilterFields(form.fieldsets, filter_fields);
  // filtFields are the actual field model represented by filter_fields
  // They need to be reloaded if an quota option is changed via the following function:
  const reloadFormQuotaData = () => {
    // Reloads filtFields / form.active status when number of subs change,
    // or if updating an informerField response option to get updated quota numbers.
    revalidator.revalidate();
  };

  const user = { ...userCan(current_user, permission) };

  const {
    container,
    display_name,
    name,
    course_id,
    course_validate,
    password,
    active,
    created_at,
    updated_at,
  } = form;

  return (
    <UserContext.Provider value={user}>
      <Breadcrumbs
        form={{ name: name, id: form.id }}
        container={{ name: container.name, id: container.id }}
      />
      <div className='form'>
        <div className='details'>
          <div className='controls'>
            <Icon
              url={`/manage/forms/${form.id}/mailers`}
              icon='envelope'
              title={(user.isAdmin ? 'Edit' : 'View') + ' Mailers'}
              className='edit'
              isLink
            />
            <Icon
              url={`/manage/forms/${form.id}/edit`}
              icon='cogs'
              title={user.isAdmin ? 'Edit Form' : 'View Form Details'}
              className='edit'
              isLink
            />
          </div>
          <Row name='Display Name:' value={display_name} />
          <Row name='Name:' value={name} />
          <Row name='Active:' value={active ? 'Yes' : 'No'} />
          <Row
            name='Course Validated:'
            value={course_validate ? 'Yes' : 'No'}
          />
          {course_validate && (
            <>
              <Row name='Course Name/URL:' value={course_id} />
              <Row name='Course Password:' value={password} />
            </>
          )}
          <Permalink containerSlug={container.slug} formSlug={form.slug} />
          <TimeStamp title='Created' value={created_at} />
          <TimeStamp title='Updated' value={updated_at} />
          {form.form_meta_entries.length > 0 && (
            <FormContext.Provider
              value={{ formMetaEntries: form.form_meta_entries }}
            >
              <FormMetaDataEntries
                show={showMeta}
                toggle={() => setShowMeta(!showMeta)}
              />
            </FormContext.Provider>
          )}
        </div>

        <FilterFieldsContext.Provider
          value={{
            filtFields,
            filter_fields,
            reloadFormQuotaData,
            filterByCustom,
            customFiltersAvailable,
          }}
        >
          <SubmissionsIndex
            form={form}
            informerField={informerField}
            reloadFormQuotaData={reloadFormQuotaData}
            setFilterByCustom={setFilterByCustom}
          />
        </FilterFieldsContext.Provider>
      </div>
    </UserContext.Provider>
  );
};

export default FormShowSubmissions;
